import type { Vec2, Ring } from '../../geo/types';

/**
 * Exact plan offsets for building outlines. Mitred joins keep every offset edge parallel
 * to its source edge, so walls, parapets and roof eaves line up without clipping noise.
 */

/** Sharp bends clamp the mitre so an offset never shoots past about 2.8 times its distance. */
const MITRE_FLOOR = 0.25;

function leftNormal(a: Vec2, b: Vec2): Vec2 {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  return [-dy / len, dx / len];
}

function mitre(p: Vec2, n1: Vec2, n2: Vec2, distance: number): Vec2 {
  const k = distance / Math.max(1 + n1[0] * n2[0] + n1[1] * n2[1], MITRE_FLOOR);
  return [p[0] + (n1[0] + n2[0]) * k, p[1] + (n1[1] + n2[1]) * k];
}

/** Moves every edge of a counter-clockwise convex ring inward by `distance`. */
export function insetConvexRing(ring: Ring, distance: number): Ring {
  const n = ring.length;
  return ring.map((p, i) => {
    const prev = ring[(i + n - 1) % n]!;
    const next = ring[(i + 1) % n]!;
    return mitre(p, leftNormal(prev, p), leftNormal(p, next), distance);
  });
}

/** Offsets an open polyline to its left (negative distance: right) with mitred joins. */
export function offsetPolyline(points: readonly Vec2[], distance: number): Vec2[] {
  const last = points.length - 1;
  return points.map((p, i): Vec2 => {
    const n1 = leftNormal(points[Math.max(0, i - 1)]!, points[Math.max(1, i)]!);
    const n2 = leftNormal(points[Math.min(i, last - 1)]!, points[Math.min(i + 1, last)]!);
    return mitre(p, n1, n2, distance);
  });
}

/** Counter-clockwise outline of a polyline stroked to `halfWidth` on both sides. */
export function strokeRing(points: readonly Vec2[], halfWidth: number): Ring {
  const right = offsetPolyline(points, -halfWidth);
  const left = offsetPolyline(points, halfWidth);
  return [...right, ...left.reverse()];
}

function mergeIntervals(cuts: readonly (readonly [number, number])[]): [number, number][] {
  const sorted = [...cuts].sort((a, b) => a[0] - b[0]);
  return sorted.reduce<[number, number][]>((merged, [s0, s1]) => {
    const top = merged[merged.length - 1];
    if (top && s0 <= top[1]) return [...merged.slice(0, -1), [top[0], Math.max(top[1], s1)]];
    return [...merged, [s0, s1]];
  }, []);
}

/** Arc-length intervals of [0, total] left after the cuts, without stubs below minLength. */
export function pathIntervals(
  total: number,
  cuts: readonly (readonly [number, number])[],
  minLength: number
): [number, number][] {
  const kept: [number, number][] = [];
  let start = 0;
  for (const [s0, s1] of mergeIntervals(cuts)) {
    kept.push([start, Math.max(start, s0)]);
    start = Math.max(start, s1);
  }
  kept.push([start, total]);
  return kept.filter(([a, b]) => b - a >= minLength);
}

function lerp(a: Vec2, b: Vec2, t: number): Vec2 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/** The part of a polyline between arc lengths s0 and s1, keeping the vertices in between. */
export function subPath(points: readonly Vec2[], s0: number, s1: number): Vec2[] {
  const out: Vec2[] = [];
  let walked = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const end = walked + len;
    if (end > s0 && walked < s1 && len > 0) {
      if (out.length === 0) out.push(lerp(a, b, Math.max(0, (s0 - walked) / len)));
      out.push(end <= s1 ? b : lerp(a, b, (s1 - walked) / len));
    }
    walked = end;
  }
  return out;
}
